import axios from 'axios';
import { API_URL } from '../../config/api';
export interface AnalyticsData {
  user_growth: { month: string; users: number }[];
  message_stats: { day: string; count: number }[];
  category_data: { name: string; value: number; color: string }[];
  daily_activity: { hour: string; activity: number }[];
  stats: { total_growth: string; daily_active: number; message_volume: number; new_places: number };
  insights: { trend_percentage: number; most_active_hour: string; avg_response_time: string };
}
export interface Point { label: string; value: number }
export const numberLabel = (value: number | undefined) => value === undefined ? '—' : value.toLocaleString('tr-TR');
const count = (value: unknown) => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
export function validData(raw: unknown): raw is AnalyticsData {
  if (!raw || typeof raw !== 'object') return false;
  const d = raw as AnalyticsData;
  return Array.isArray(d.user_growth) && d.user_growth.every(row => row && typeof row.month === 'string' && count(row.users)) &&
    Array.isArray(d.message_stats) && d.message_stats.every(row => row && typeof row.day === 'string' && count(row.count)) &&
    Array.isArray(d.category_data) && d.category_data.every(row => row && typeof row.name === 'string' && count(row.value) && typeof row.color === 'string' && /^#[0-9a-f]{6}$/i.test(row.color)) &&
    Array.isArray(d.daily_activity) && d.daily_activity.every(row => row && typeof row.hour === 'string' && /^(0[048]|12|16|20)$/.test(row.hour) && count(row.activity)) &&
    !!d.stats && typeof d.stats.total_growth === 'string' && /^[+-]?\d+(?:\.\d+)?%$/.test(d.stats.total_growth) && [d.stats.daily_active, d.stats.message_volume, d.stats.new_places].every(count) &&
    !!d.insights && typeof d.insights.trend_percentage === 'number' && Number.isFinite(d.insights.trend_percentage) && typeof d.insights.most_active_hour === 'string' && typeof d.insights.avg_response_time === 'string';
}
export async function fetchAnalytics() {
  const { data } = await axios.get<unknown>(`${API_URL}/admin/analytics`);
  if (!validData(data)) throw new Error('Invalid analytics snapshot');
  return data;
}
export const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);
export function signedPercent(value: number) { return `${value > 0 ? '+' : ''}${value.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}%`; }
export function monthlyChange(data: AnalyticsData) {
  // This compares monthly registration counts, never cumulative user growth.
  return data.user_growth.length > 1 && data.user_growth[0].users > 0 ? signedPercent(Number(data.stats.total_growth.replace('%', ''))) : '—';
}
export function peakStart(raw: string) { const match = /^([01]\d|2[0-3]):00-(?:[01]\d|2[0-3]):00$/.exec(raw); return match ? `${match[1]}:00` : '—'; }
export function durationLabel(raw: string) {
  if (!raw || raw === 'N/A') return '—';
  const match = /^(\d+(?:\.\d+)?) dakika$/.exec(raw);
  return match ? `${Number(match[1]).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} dk` : raw;
}
export const monthPoints = (data: AnalyticsData): Point[] => data.user_growth.map(row => ({ label: row.month, value: row.users }));
// The server's weekday names are offset; use the guaranteed chronological row order.
export const messagePoints = (data: AnalyticsData): Point[] => data.message_stats.map((row, index) => ({ label: `${index + 1}. gün`, value: row.count }));
export function hourLabel(hour: string) { return `${hour}:00–${String(Number(hour) + 4).padStart(2, '0')}:00`; }
export const hourPoints = (data: AnalyticsData): Point[] => data.daily_activity.map(row => ({ label: hourLabel(row.hour), value: row.activity }));
